import React, { useState, useMemo } from 'react';
import Fuse from 'fuse.js';
import { FileText, Book, Zap, HelpCircle } from 'lucide-react';
import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from '@/components/ui/command';
import { documentIndex, fuseOptions, getDocsByCategory } from '@/data/docs';

/**
 * Get icon component based on document category
 */
function getCategoryIcon(category) {
  switch (category) {
    case 'quickstart':
      return Zap;
    case 'rules':
    case 'notifications':
    case 'activity':
      return FileText;
    default:
      return Book;
  }
}

/**
 * Get color class based on document category
 */
function getCategoryColor(category) {
  switch (category) {
    case 'quickstart':
      return 'text-amber-500';
    case 'rules':
      return 'text-purple-500';
    case 'notifications':
      return 'text-blue-500';
    case 'activity':
      return 'text-green-500';
    default:
      return 'text-slate-500';
  }
}

/**
 * HelpSearch - Fuzzy search component for documentation
 *
 * @param {Function} onSelectArticle - Callback when user selects a search result
 * @param {string} className - Additional styling
 * @param {string} userRole - Current user's role for filtering docs
 */
export function HelpSearch({ onSelectArticle, className = '', userRole = 'admin' }) {
  const [query, setQuery] = useState('');

  // Initialize Fuse.js search instance
  const fuse = useMemo(() => {
    // Filter documents by user role
    const roleFilteredDocs = documentIndex.filter(doc => doc.roles.includes(userRole));
    return new Fuse(roleFilteredDocs, fuseOptions);
  }, [userRole]);

  // Get search results or categorized view
  const searchResults = useMemo(() => {
    if (query.trim().length < 2) {
      return null; // Show categories instead
    }
    return fuse.search(query);
  }, [query, fuse]);

  // Get categorized documents for empty query view
  const categorizedDocs = useMemo(() => {
    const quickstarts = getDocsByCategory('quickstart').filter(d => d.roles.includes(userRole));
    const features = documentIndex.filter(d =>
      ['rules', 'notifications', 'activity'].includes(d.category) &&
      d.roles.includes(userRole)
    );
    const general = getDocsByCategory('general').filter(d => d.roles.includes(userRole));

    return { quickstarts, features, general };
  }, [userRole]);

  const handleSelect = (doc) => {
    onSelectArticle(doc);
  };

  return (
    <Command className={`rounded-lg border shadow-md ${className}`}>
      <CommandInput
        placeholder="Search help... (e.g., 'how to create rule')"
        value={query}
        onValueChange={setQuery}
      />
      <CommandList className="max-h-[400px]">
        <CommandEmpty>No results found. Try different keywords.</CommandEmpty>

        {/* Show search results when query entered */}
        {searchResults && searchResults.length > 0 && (
          <CommandGroup heading="Search Results">
            {searchResults.map(({ item: doc, score }) => {
              const Icon = getCategoryIcon(doc.category);
              const colorClass = getCategoryColor(doc.category);
              return (
                <CommandItem
                  key={doc.id}
                  value={doc.id}
                  onSelect={() => handleSelect(doc)}
                  className="cursor-pointer"
                >
                  <Icon className={`mr-2 h-4 w-4 ${colorClass}`} />
                  <div className="flex flex-col">
                    <span className="font-medium">{doc.title}</span>
                    <span className="text-xs text-slate-500 line-clamp-1">
                      {doc.description}
                    </span>
                  </div>
                </CommandItem>
              );
            })}
          </CommandGroup>
        )}

        {/* Show categorized view when no query */}
        {!searchResults && (
          <>
            {categorizedDocs.quickstarts.length > 0 && (
              <CommandGroup heading="Quick Starts">
                {categorizedDocs.quickstarts.map((doc) => {
                  const Icon = getCategoryIcon(doc.category);
                  const colorClass = getCategoryColor(doc.category);
                  return (
                    <CommandItem
                      key={doc.id}
                      value={doc.id}
                      onSelect={() => handleSelect(doc)}
                      className="cursor-pointer"
                    >
                      <Icon className={`mr-2 h-4 w-4 ${colorClass}`} />
                      <div className="flex flex-col">
                        <span className="font-medium">{doc.title}</span>
                        <span className="text-xs text-slate-500 line-clamp-1">
                          {doc.description}
                        </span>
                      </div>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            )}

            {categorizedDocs.features.length > 0 && (
              <CommandGroup heading="Features">
                {categorizedDocs.features.map((doc) => {
                  const Icon = getCategoryIcon(doc.category);
                  const colorClass = getCategoryColor(doc.category);
                  return (
                    <CommandItem
                      key={doc.id}
                      value={doc.id}
                      onSelect={() => handleSelect(doc)}
                      className="cursor-pointer"
                    >
                      <Icon className={`mr-2 h-4 w-4 ${colorClass}`} />
                      <div className="flex flex-col">
                        <span className="font-medium">{doc.title}</span>
                        <span className="text-xs text-slate-500 line-clamp-1">
                          {doc.description}
                        </span>
                      </div>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            )}

            {categorizedDocs.general.length > 0 && (
              <CommandGroup heading="Reference">
                {categorizedDocs.general.map((doc) => {
                  const Icon = getCategoryIcon(doc.category);
                  const colorClass = getCategoryColor(doc.category);
                  return (
                    <CommandItem
                      key={doc.id}
                      value={doc.id}
                      onSelect={() => handleSelect(doc)}
                      className="cursor-pointer"
                    >
                      <Icon className={`mr-2 h-4 w-4 ${colorClass}`} />
                      <div className="flex flex-col">
                        <span className="font-medium">{doc.title}</span>
                        <span className="text-xs text-slate-500 line-clamp-1">
                          {doc.description}
                        </span>
                      </div>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            )}
          </>
        )}
      </CommandList>
    </Command>
  );
}

export default HelpSearch;
